import { DayPilot } from 'daypilot-pro-react'
import { sortBy, uniq } from 'lodash'
import { insertIf, getKeysWithTruthyValues, canCreatePunchCard } from 'common'

import { hasAccessToThis } from 'services/Utilities'
import { ACTIONS, ActionsTypes } from 'Manager/Schedule/SchedulerContextMenu/constants'
import { MultiItems, MultiItemsMobile, SingleItems, SingleItemsMobile } from 'Manager/Schedule/SchedulerContextMenu/hooks'

import { t } from 'translations'

export const getMenuTitle = (selectedEvents: MData.SchedulerEvent[]) => {
  const selectedTimeOffCount = selectedEvents.filter(({ isTimeOff }) => isTimeOff).length
  const selectedAbsencesCount = selectedEvents.filter(({ isAbsence }) => isAbsence).length
  const selectedShiftsCount = selectedEvents.filter(({ isTimeOff, isAbsence }) => !isTimeOff && !isAbsence).length

  const menuTitle: string[] = []
  if (selectedShiftsCount) menuTitle.push(t('manager.schedule.CONTEXT_MENU.SELECTED', { count: selectedShiftsCount }))
  if (selectedTimeOffCount) menuTitle.push(t('manager.schedule.CONTEXT_MENU.SELECTED_TIMEOFF', { count: selectedTimeOffCount }))
  if (selectedAbsencesCount) menuTitle.push(t('manager.schedule.CONTEXT_MENU.SELECTED_ABSENCES', { count: selectedAbsencesCount }))
  return menuTitle.join('<br/>')
}

export const getActionsSingleEvent = (
  event: MData.SchedulerEvent,
  { forceCancelledShifts, scheduleTemplatesById }: { forceCancelledShifts?: boolean; scheduleTemplatesById: MStore.ResourceMap['scheduleTemplatesById'] }
) => {
  const {
    isAbsence,
    isTimeOff,
    finalized,
    employeeId,
    published,
    cancelled,
    contactListId,
    templateId,
    attendanceId,
    timeOffId,
    punchCardId,
    comments,
    start,
    startTimezone
  } = event

  const isShift = !isAbsence && !isTimeOff
  const isAssignedShift = isShift && !!employeeId
  const hasComments = comments?.length > 0
  const hasOptionToCreatePunchCard = canCreatePunchCard({ start, startTimezone })

  const timeOffActions = {
    [ACTIONS.NOTIFY_TIMEOFF]: hasAccessToThis('manageTimeOff') && !published,
    [ACTIONS.GO_TO_TIMEOFF]: hasAccessToThis('manageTimeOff'),
    [ACTIONS.OPEN_VACATION_COMMENTS]: hasAccessToThis('manageTimeOff') && hasComments
  }

  const attendanceActions = {
    [ACTIONS.GO_TO_ATTENDANCE]: hasAccessToThis('attendanceSeeOthers'),
    [ACTIONS.OPEN_LEAVE_COMMENTS]: hasAccessToThis('manageTimeOff') && hasComments
  }

  const coreShiftActions = {
    [ACTIONS.COPY]: hasAccessToThis('scheduleCreate'),
    [ACTIONS.EDIT]: !finalized && hasAccessToThis('scheduleModify'),
    [ACTIONS.EDIT_USER]: !finalized && hasAccessToThis('scheduleOnlyAssignEmployee'),
    [ACTIONS.DELETE]: !finalized && !forceCancelledShifts && hasAccessToThis('scheduleDelete'),
    [ACTIONS.RESTORE]: cancelled && hasAccessToThis('scheduleCancel'),
    [ACTIONS.CANCEL]: !finalized && hasAccessToThis('scheduleCancel'),
    [ACTIONS.CREATE_LIST]: hasAccessToThis('scheduleListCreation'),
    [ACTIONS.GO_TO_CONTACT_LIST]: contactListId && hasAccessToThis('contactListSee'),
    [ACTIONS.GO_TO_SCHEDULE_TEMPLATE]: templateId && scheduleTemplatesById[templateId] && hasAccessToThis('scheduleTemplateSee')
  }

  const assignedShiftActions = {
    [ACTIONS.PUBLISH]: !published && hasAccessToThis('schedulePublish'),
    [ACTIONS.UNPUBLISH]: published && hasAccessToThis('schedulePublish'),
    [ACTIONS.NOTIFY_SHIFT]: published && hasAccessToThis('schedulePublish'),
    [ACTIONS.CREATE_ATTENDANCE]: hasAccessToThis('manageAttendance'),
    [ACTIONS.CREATE_PUNCHCARD]: hasAccessToThis('punchCardManage') && hasOptionToCreatePunchCard,
    [ACTIONS.GO_TO_USER]: hasAccessToThis('manageUser'),
    [ACTIONS.GO_TO_USER_SCHEDULE]: hasAccessToThis('scheduleSeeOthers'),
    [ACTIONS.UNASSIGN_EMPLOYEE]: hasAccessToThis('scheduleAssignEmployee'),
    // These are pretty much not used anymore so Francis wants to hide them to see if people are complaining
    // [ACTIONS.UNFINALIZE]: finalized && hasAccessToThis('scheduleModify'),
    // [ACTIONS.FINALIZE]: !finalized && hasAccessToThis('scheduleModify'),
    [ACTIONS.GO_TO_ATTENDANCE]: attendanceId && hasAccessToThis('attendanceSeeOthers'),
    [ACTIONS.GO_TO_PUNCHCARD]: punchCardId && hasAccessToThis('punchCardSee'),
    [ACTIONS.GO_TO_TIMEOFF]: timeOffId && hasAccessToThis('manageTimeOff')
  }

  const actions = [
    ...insertIf(isTimeOff, ...getKeysWithTruthyValues(timeOffActions)),
    ...insertIf(isAbsence, ...getKeysWithTruthyValues(attendanceActions)),
    ...insertIf(isShift, ...getKeysWithTruthyValues(coreShiftActions)),
    ...insertIf(isAssignedShift, ...getKeysWithTruthyValues(assignedShiftActions))
  ] as ActionsTypes[]

  return actions
}

export const getActionsMultiEvents = (
  events: MData.SchedulerEvent[],
  { forceCancelledShifts, editFinalizedShifts }: { forceCancelledShifts?: boolean; editFinalizedShifts?: boolean }
) => {
  const shifts = events.filter(s => !s.isTimeOff && !s.isAbsence)

  const hasShifts = shifts.length > 0
  const hasShiftsAssigned = shifts.some(s => s.employeeId)
  const hasTimeOffs = events.some(s => s.isTimeOff)

  const someFinalized = shifts.some(s => s.finalized)
  const allFinalized = shifts.every(s => s.finalized)
  const allCancelled = shifts.every(s => s.cancelled)
  const someCancelled = shifts.some(s => s.cancelled)
  const somePublished = shifts.filter(s => !s.isTimeOff).some(s => s.published)

  const unpublishedShifts = shifts.filter(s => !s.published)
  const areSomeUnpublishedShiftsAssigned = unpublishedShifts.some(s => s.employeeId)

  const timeOffActions = {
    [ACTIONS.NOTIFY_TIMEOFF]: hasAccessToThis('scheduleConfirm')
  }

  const coreShiftActions = {
    [ACTIONS.COPY]: hasAccessToThis('scheduleCreate'),
    [ACTIONS.EDIT]: (editFinalizedShifts || !someFinalized) && hasAccessToThis('scheduleModify'),
    [ACTIONS.EDIT_USER]: !allFinalized && hasAccessToThis('scheduleOnlyAssignEmployee'),
    [ACTIONS.DELETE]: !forceCancelledShifts && hasAccessToThis('scheduleDelete'),
    [ACTIONS.RESTORE]: someCancelled && hasAccessToThis('scheduleCancel'),
    [ACTIONS.CANCEL]: !allCancelled && hasAccessToThis('scheduleCancel'),
    [ACTIONS.CREATE_LIST]: hasAccessToThis('scheduleListCreation')
  }

  const assignedShiftActions = {
    [ACTIONS.PUBLISH]: areSomeUnpublishedShiftsAssigned && hasAccessToThis('schedulePublish'),
    [ACTIONS.UNPUBLISH]: somePublished && hasAccessToThis('schedulePublish'),
    [ACTIONS.NOTIFY_SHIFT]: somePublished && hasAccessToThis('schedulePublish'),
    [ACTIONS.UNASSIGN_EMPLOYEE]: hasAccessToThis('scheduleAssignEmployee')
    // [ACTIONS.UNFINALIZE]: someFinalized && hasAccessToThis('scheduleModify'),
    // [ACTIONS.FINALIZE]: !allFinalized && hasAccessToThis('scheduleModify')
  }

  const actions = [
    ...insertIf(hasTimeOffs, ...getKeysWithTruthyValues(timeOffActions)),
    ...insertIf(hasShifts, ...getKeysWithTruthyValues(coreShiftActions)),
    ...insertIf(hasShifts && hasShiftsAssigned, ...getKeysWithTruthyValues(assignedShiftActions))
  ] as ActionsTypes[]

  return actions
}

export const getActionsMultiEventsMobile = (events: MData.SchedulerEvent[], { editFinalizedShifts }: { editFinalizedShifts?: boolean }) => {
  const shifts = events.filter(s => !s.isTimeOff && !s.isAbsence)

  const hasShifts = shifts.length > 0
  const hasShiftsAssigned = shifts.some(s => s.employeeId)

  const someFinalized = shifts.some(s => s.finalized)
  const allCancelled = shifts.every(s => s.cancelled)
  const someCancelled = shifts.some(s => s.cancelled)
  const somePublished = shifts.filter(s => !s.isTimeOff).some(s => s.published)

  const unpublishedShifts = shifts.filter(s => !s.published)
  const areSomeUnpublishedShiftsAssigned = unpublishedShifts.some(s => s.employeeId)

  const coreShiftActions = {
    [ACTIONS.EDIT]: (editFinalizedShifts || !someFinalized) && hasAccessToThis('scheduleModify'),
    [ACTIONS.CANCEL]: !allCancelled && hasAccessToThis('scheduleCancel'),
    [ACTIONS.RESTORE]: someCancelled && hasAccessToThis('scheduleCancel'),
    [ACTIONS.CREATE_LIST]: hasAccessToThis('scheduleListCreation')
  }

  const assignedShiftActions = {
    [ACTIONS.PUBLISH]: areSomeUnpublishedShiftsAssigned && hasAccessToThis('schedulePublish'),
    [ACTIONS.UNPUBLISH]: somePublished && hasAccessToThis('schedulePublish'),
    [ACTIONS.UNASSIGN_EMPLOYEE]: hasAccessToThis('scheduleAssignEmployee')
  }

  const actions = [
    ...insertIf(hasShifts, ...getKeysWithTruthyValues(coreShiftActions)),
    ...insertIf(hasShifts && hasShiftsAssigned, ...getKeysWithTruthyValues(assignedShiftActions))
  ] as ActionsTypes[]

  return actions
}

type getMenuItemsOptions = {
  isMultiSelection: boolean
  isMobile: boolean
  scheduleTemplatesById: MStore.ResourceMap['scheduleTemplatesById']
  forceCancelledShifts?: boolean
  editFinalizedShifts?: boolean
}

export const getMenuItems = (
  events: MData.SchedulerEvent[],
  items: SingleItemsMobile | SingleItems | MultiItemsMobile | MultiItems,
  { isMultiSelection, isMobile, scheduleTemplatesById, forceCancelledShifts, editFinalizedShifts }: getMenuItemsOptions
) => {
  const singleEventActions = getActionsSingleEvent(events[0], { forceCancelledShifts, scheduleTemplatesById })
  const multiEventsActionsDesktop = getActionsMultiEvents(events, { forceCancelledShifts, editFinalizedShifts })
  const multiEventsActionsMobile = getActionsMultiEventsMobile(events, { editFinalizedShifts })

  const multiEventActions = isMobile ? multiEventsActionsMobile : multiEventsActionsDesktop
  const eventsMenuItems = (isMultiSelection ? multiEventActions : singleEventActions).map(action => items[action]).filter(Boolean)

  const menuItems = !eventsMenuItems.length
    ? [{ text: t('manager.schedule.CONTEXT_MENU.NO_OPTIONS'), disabled: true }]
    : sortMenuItems(eventsMenuItems, { isMobile })

  return menuItems
}

export type ScheduleMenuItem = DayPilot.MenuItemData & {
  submenu?: 'open' | 'advanced'
  order?: number
}

const createSubmenus = (menuItems: ScheduleMenuItem[]) => {
  const submenuNames: string[] = uniq(menuItems.map(item => item.submenu)).filter(Boolean)

  const submenus = submenuNames.map(submenuName => {
    return {
      text: t(`manager.schedule.CONTEXT_MENU.${submenuName.toUpperCase()}`),
      icon: 'fas fa-chevron-right',
      order: 6,
      items: sortBy(
        menuItems.filter(item => item.submenu === submenuName),
        ['order']
      )
    }
  })
  return submenus
}

const sortMenuItems = (menuItems: ScheduleMenuItem[], { isMobile }: { isMobile: boolean }) => {
  if (isMobile) return sortBy(menuItems, ['order'])

  const submenus = createSubmenus(menuItems)
  const otherItems = menuItems.filter(item => !item.submenu)

  const sortedMenuItems: ScheduleMenuItem[] = [...submenus, ...otherItems]

  // Adding separators
  if (sortedMenuItems.length > 1) sortedMenuItems.push({ text: '-', order: 5 })
  if (sortedMenuItems.some(item => item.order === 8)) sortedMenuItems.push({ text: '-', order: 7 })

  return sortBy(sortedMenuItems, ['order'])
}
